import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Flex, HStack, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';

import {
  ScheduledBankTransferEndDateDeterminedBy,
  ScheduledBankTransferFrequency,
} from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Radio from 'components/_core/form/Radio';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';
import { useBankTransferContext } from 'features/Payments/Pages/Transfers/contexts';
import * as Layout from 'layouts/ContentLayout';

import {
  endDateDeterminedByLocales,
  frequencyLocales,
} from '../../../commonLocales';
import {
  getLastDueDateForDisplay,
  getTomorrowDate,
  MAXIMUM_TRANSFER_AMOUNT_EUROS,
  ScheduledBankTransferEndDateDeterminedByOption,
} from '../../../utils';
import locales from './locales';
import {
  ScheduledTransferSettingsFormSchema,
  scheduledTransferSettingsFormSchema,
} from './scheduledTransferSettingsForm.schema';

const FREQUENCY_OPTIONS: ScheduledBankTransferFrequency[] = [
  ScheduledBankTransferFrequency.Once,
  ScheduledBankTransferFrequency.EveryWeek,
  ScheduledBankTransferFrequency.EveryMonth,
  ScheduledBankTransferFrequency.EveryYear,
];

interface ScheduledTransferSettingsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isAmountAboveThreshold: boolean;
}

const ScheduledTransferSettingsSidebar: FC<
  ScheduledTransferSettingsSidebarProps
> = ({ isAmountAboveThreshold, isOpen, onClose }) => {
  const {
    bankTransfer: { schedule },
    setBankTransfer,
  } = useBankTransferContext('setExternalDetails');

  const {
    endDateDeterminedBy,
    frequency,
    lastDueDate,
    nextDueDate,
    remainingNumberOfOccurrences,
  } = schedule ?? {};

  const tomorrow = getTomorrowDate();

  const form = useForm<ScheduledTransferSettingsFormSchema>({
    defaultValues: {
      endDateDeterminedBy:
        endDateDeterminedBy ??
        ScheduledBankTransferEndDateDeterminedBy.NumberOfOccurrences,
      frequency: frequency ?? ScheduledBankTransferFrequency.EveryMonth,
      isAmountAboveThreshold,
      lastDueDate: lastDueDate ?? tomorrow.toISOString(),
      nextDueDate: nextDueDate ?? tomorrow.toISOString(),
      numberOfOccurrences: remainingNumberOfOccurrences ?? 1,
    },
    resolver: zodResolver(scheduledTransferSettingsFormSchema),
  });

  useEffect(() => {
    form.setValue('isAmountAboveThreshold', isAmountAboveThreshold);

    if (frequency) {
      return;
    }
    form.setValue(
      'frequency',
      isAmountAboveThreshold
        ? ScheduledBankTransferFrequency.Once
        : ScheduledBankTransferFrequency.EveryMonth,
    );
  }, [form, frequency, isAmountAboveThreshold]);

  if (!isOpen) {
    return null;
  }

  const onSubmit = () => {
    const values = form.getValues();
    setBankTransfer((prevState) => ({
      ...prevState,
      schedule: {
        endDateDeterminedBy:
          values.endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.Never
            ? undefined
            : values.endDateDeterminedBy,
        frequency: values.frequency,
        lastDueDate:
          values.endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate
            ? values.lastDueDate
            : undefined,
        nextDueDate: values.nextDueDate,
        remainingNumberOfOccurrences:
          values.endDateDeterminedBy ===
          ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences
            ? Number(values.numberOfOccurrences)
            : undefined,
      },
    }));
    onClose();
  };

  const onDeleteSettings = () => {
    setBankTransfer((prevState) => ({
      ...prevState,
      schedule: undefined,
    }));
    form.reset();
    onClose();
  };

  return (
    <Layout.Sidebar
      bg="white"
      borderLeft="1px solid"
      borderLeftColor="border"
      isScrollable
    >
      <VStack
        align="stretch"
        flex={1}
        paddingBottom="space-16"
        paddingTop="space-24"
        paddingX="space-24"
        spacing="space-32"
        width="100%"
      >
        <IconButton
          alignSelf="flex-end"
          aria-label=""
          icon="cross"
          onClick={onClose}
          variant="secondary"
        />

        <Flex
          animate={{ opacity: 1, y: 0 }}
          as={motion.div}
          flex={1}
          initial={{ opacity: 0, y: -10 }}
        >
          <VStack
            alignItems="stretch"
            flex={1}
            justify="space-between"
            spacing="space-32"
          >
            <Text fontSize="font-18" fontWeight="weight-500" variant="primary">
              {locales.title}
            </Text>
            <VStack alignItems="stretch" flex={1} spacing="space-24">
              <Controller
                control={form.control}
                name="nextDueDate"
                render={({ field }) => (
                  <Form.Field
                    error={form.formState.errors.nextDueDate?.message}
                    label={locales.nextDueDate}
                  >
                    <Form.SunshineInputDatePicker
                      modifiers={{
                        disabled: {
                          before: tomorrow,
                        },
                      }}
                      onChange={(date) => {
                        date && field.onChange(date.toISOString());
                        form.watch('endDateDeterminedBy') ===
                          ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate &&
                          form.trigger('lastDueDate');
                      }}
                      value={field.value ? new Date(field.value) : undefined}
                      withSetTodayButton={false}
                    />
                  </Form.Field>
                )}
              />
              <Box>
                <Form.Field label={locales.frequency}>
                  <Controller
                    control={form.control}
                    name="frequency"
                    render={({ field: { onChange, value } }) => (
                      <Radio.Group
                        _first={{
                          paddingTop: 'space-12',
                        }}
                        value={value ?? undefined}
                      >
                        {FREQUENCY_OPTIONS.map((frequencyOption) => (
                          <Radio
                            isDisabled={
                              isAmountAboveThreshold &&
                              frequencyOption !==
                                ScheduledBankTransferFrequency.Once
                            }
                            key={frequencyOption}
                            onChange={onChange}
                            value={frequencyOption}
                          >
                            <Radio.Title
                              variant={
                                isAmountAboveThreshold &&
                                frequencyOption !==
                                  ScheduledBankTransferFrequency.Once
                                  ? 'disabled'
                                  : 'primary'
                              }
                            >
                              {frequencyLocales[frequencyOption]}
                            </Radio.Title>
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                  />
                </Form.Field>
                {isAmountAboveThreshold ? (
                  <Callout
                    marginTop="space-16"
                    variant={
                      form.formState.errors.frequency?.message
                        ? 'error'
                        : 'info'
                    }
                  >
                    <Callout.Content>
                      {locales.formatString(
                        locales.amountAboveThreshold,
                        formatAmount({
                          currency: 'EUR',
                          forcePlusSign: false,
                          value: MAXIMUM_TRANSFER_AMOUNT_EUROS,
                          withNoMinorUnitDigits: true,
                        }),
                      )}
                    </Callout.Content>
                  </Callout>
                ) : null}
              </Box>
              {form.watch('frequency') !==
              ScheduledBankTransferFrequency.Once ? (
                <Flex
                  animate={{ opacity: 1, y: 0 }}
                  as={motion.div}
                  flex={1}
                  initial={{ opacity: 0, y: -10 }}
                >
                  <Form.Field label={locales.lastDueDate}>
                    <Controller
                      control={form.control}
                      name="endDateDeterminedBy"
                      render={({ field }) => (
                        <Radio.Group
                          _first={{
                            paddingTop: 'space-12',
                          }}
                          onChange={(value) => {
                            if (
                              value !==
                              ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences
                            ) {
                              form.clearErrors('numberOfOccurrences');
                            }
                            if (
                              value !==
                              ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate
                            ) {
                              form.clearErrors('lastDueDate');
                            }
                          }}
                          value={field.value ?? undefined}
                        >
                          <Radio
                            isDisabled={isAmountAboveThreshold}
                            key={
                              ScheduledBankTransferEndDateDeterminedByOption.Never
                            }
                            onChange={field.onChange}
                            value={
                              ScheduledBankTransferEndDateDeterminedByOption.Never
                            }
                          >
                            <Radio.Title>
                              {
                                endDateDeterminedByLocales[
                                  ScheduledBankTransferEndDateDeterminedByOption
                                    .Never
                                ]
                              }
                            </Radio.Title>
                          </Radio>
                          <Form.Field
                            error={form.formState.errors.lastDueDate?.message}
                          >
                            <Radio
                              isDisabled={isAmountAboveThreshold}
                              key={
                                ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate
                              }
                              onChange={field.onChange}
                              value={
                                ScheduledBankTransferEndDateDeterminedByOption.ExplicitDate
                              }
                            >
                              <HStack>
                                <Radio.Title>
                                  {
                                    endDateDeterminedByLocales[
                                      ScheduledBankTransferEndDateDeterminedByOption
                                        .ExplicitDate
                                    ]
                                  }
                                </Radio.Title>
                                <Controller
                                  control={form.control}
                                  name="lastDueDate"
                                  render={({ field: dateField }) => (
                                    <Form.SunshineInputDatePicker
                                      inputProps={{
                                        isDisabled: isAmountAboveThreshold,
                                        maxW: '114px',
                                        textAlign: 'center',
                                      }}
                                      modifiers={{
                                        disabled: {
                                          before: tomorrow,
                                        },
                                      }}
                                      onChange={(date) => {
                                        date &&
                                          dateField.onChange(
                                            date.toISOString(),
                                          );
                                      }}
                                      value={
                                        dateField.value
                                          ? new Date(dateField.value)
                                          : undefined
                                      }
                                      withSetTodayButton={false}
                                    />
                                  )}
                                />
                              </HStack>
                            </Radio>
                          </Form.Field>

                          <Form.Field
                            error={
                              form.formState.errors.numberOfOccurrences?.message
                            }
                            sx={{
                              '& .chakra-form__helper-text': {
                                paddingLeft: 'space-36',
                              },
                            }}
                            {...(form.watch('endDateDeterminedBy') ===
                              ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences && {
                              hint: locales.formatString(
                                locales.lastDueDateBasedOnOccurrences,
                                getLastDueDateForDisplay({
                                  endDateDeterminedBy: form.watch(
                                    'endDateDeterminedBy',
                                  ),
                                  frequency: form.watch('frequency'),
                                  lastDueDate: form.watch('lastDueDate'),
                                  nextDueDate: form.watch('nextDueDate'),
                                  numberOfOccurrences: form.watch(
                                    'numberOfOccurrences',
                                  ),
                                }),
                              ),
                            })}
                          >
                            <Radio
                              isDisabled={isAmountAboveThreshold}
                              key={
                                ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences
                              }
                              onChange={field.onChange}
                              value={
                                ScheduledBankTransferEndDateDeterminedByOption.NumberOfOccurrences
                              }
                            >
                              <HStack>
                                <Radio.Title>
                                  {
                                    endDateDeterminedByLocales[
                                      ScheduledBankTransferEndDateDeterminedByOption
                                        .NumberOfOccurrences
                                    ].after
                                  }
                                </Radio.Title>
                                <Controller
                                  control={form.control}
                                  name="numberOfOccurrences"
                                  render={({ field: numberField }) => (
                                    <Form.Input
                                      inputMode="numeric"
                                      isDisabled={isAmountAboveThreshold}
                                      margin="auto"
                                      onChange={(e) => {
                                        if (
                                          e.target.value?.toString().length <= 5
                                        ) {
                                          numberField.onChange(e.target.value);
                                        }
                                      }}
                                      onClick={(e) => {
                                        e.currentTarget.focus();
                                      }}
                                      textAlign="center"
                                      type="number"
                                      value={numberField.value}
                                      w={`${Math.max(
                                        5,
                                        numberField.value.toString().length + 3,
                                      )}ch`}
                                    />
                                  )}
                                />
                                <Radio.Title>
                                  {form.watch('numberOfOccurrences') > 1
                                    ? endDateDeterminedByLocales[
                                        ScheduledBankTransferEndDateDeterminedByOption
                                          .NumberOfOccurrences
                                      ].occurrences
                                    : endDateDeterminedByLocales[
                                        ScheduledBankTransferEndDateDeterminedByOption
                                          .NumberOfOccurrences
                                      ].occurrence}
                                </Radio.Title>
                              </HStack>
                            </Radio>
                          </Form.Field>
                        </Radio.Group>
                      )}
                    />
                  </Form.Field>
                </Flex>
              ) : null}
            </VStack>

            <VStack justify="stretch" paddingTop="space-16" spacing="space-16">
              <Button
                icon="tick"
                iconPosition="left"
                isExpanded
                minW="311px"
                onClick={form.handleSubmit(onSubmit)}
                variant="primary"
              >
                {locales.confirmCta}
              </Button>
              <Button
                isDisabled={!schedule}
                onClick={onDeleteSettings}
                variant="inline-secondary"
              >
                {locales.deleteSettings}
              </Button>
            </VStack>
          </VStack>
        </Flex>
      </VStack>
    </Layout.Sidebar>
  );
};

export default ScheduledTransferSettingsSidebar;
